/**
 * BehavioralInsightsCard Component
 * Displays AI-generated behavioral analysis of the client
 *
 * Features:
 * - Purple-themed card (matches AI theme)
 * - Communication style
 * - Decision pattern
 * - Interests (array of strings)
 * - Concerns (array of strings)
 * - Risk appetite
 */

import React from 'react';
import { Brain } from 'lucide-react';
import type { BehavioralInsights } from '../../../types/insights';

interface BehavioralInsightsCardProps {
  insights: BehavioralInsights;
}

export const BehavioralInsightsCard: React.FC<BehavioralInsightsCardProps> = ({
  insights,
}) => {
  return (
    <div className="bg-white border border-purple-200 rounded-lg p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Brain className="w-5 h-5 text-purple-600" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-purple-900">Behavioral Insights</h3>
      </div>

      {/* Insights Grid */}
      <div className="space-y-3">
        {/* Communication Style */}
        {insights.communication_style && (
          <div className="flex items-start gap-2">
            <span className="text-purple-500 mt-0.5 flex-shrink-0" aria-hidden="true">
              •
            </span>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Communication:</span> {insights.communication_style}
            </p>
          </div>
        )}

        {/* Decision Pattern */}
        {insights.decision_pattern && (
          <div className="flex items-start gap-2">
            <span className="text-purple-500 mt-0.5 flex-shrink-0" aria-hidden="true">
              •
            </span>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Decision-making:</span> {insights.decision_pattern}
            </p>
          </div>
        )}

        {/* Risk Appetite */}
        {insights.risk_appetite && (
          <div className="flex items-start gap-2">
            <span className="text-purple-500 mt-0.5 flex-shrink-0" aria-hidden="true">
              •
            </span>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Risk profile:</span> {insights.risk_appetite}
            </p>
          </div>
        )}

        {/* Interests */}
        {insights.interests && insights.interests.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-purple-500 mt-0.5 flex-shrink-0" aria-hidden="true">
              •
            </span>
            <div className="text-sm text-gray-700">
              <span className="font-medium">Key interests:</span>{' '}
              <span>{insights.interests.join(', ')}</span>
            </div>
          </div>
        )}

        {/* Concerns */}
        {insights.concerns && insights.concerns.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-purple-500 mt-0.5 flex-shrink-0" aria-hidden="true">
              •
            </span>
            <div className="text-sm text-gray-700">
              <span className="font-medium">Main concerns:</span>{' '}
              <span>{insights.concerns.join(', ')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BehavioralInsightsCard;
